import { AudioLines, Upload } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle, useDiscardGuard,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, Skeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { ApiError } from "@/lib/api";
import {
  audioFileProblem, audioNameFromFile, formatBytes, formatClipLength, parseTags, pickedAudio,
} from "@/lib/audio";
import { useAuth } from "@/lib/auth";
import { useAudioLibrary, useUploadAudio } from "@/lib/queries";
import { atLeast, type AudioFile } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";

/**
 * A setting's sound, chosen from the tenant's audio store (ADR-020 in platform) — never typed as a
 * path on the switch, which nobody outside the platform team could know.
 *
 * The value is the file's URL, copied off the listed file: that is what the switch fetches, so it
 * is exactly what the setting stores. A value from before the store — a path, a `local_stream://`
 * — is shown for what it is and can be replaced or cleared, not edited. An admin can upload a file
 * from inside the chooser, and it is chosen the moment it lands.
 */
export function AudioPicker({
  id, value, onChange, emptyLabel, defaultValue, disabled, "aria-describedby": describedBy,
}: {
  /** Given by `Field`, for its label: it lands on the button that opens the chooser. */
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** What no file means for this setting: "No announcement", "The switch's hold music". */
  emptyLabel: string;
  /** What the server puts back when the setting is cleared; it reads as `emptyLabel` too. */
  defaultValue?: string;
  disabled?: boolean;
  "aria-describedby"?: string;
}) {
  const { data: files } = useAudioLibrary();
  const [choosing, setChoosing] = React.useState(false);
  const picked = pickedAudio(value, files ?? [], defaultValue);
  // The field's label names the button that opens the chooser, and a label hides the button's own
  // text, so what is chosen reaches a screen reader as the button's description instead.
  const summaryId = React.useId();
  const clearId = React.useId();

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2">
      <div id={summaryId} className="min-w-0 flex-1">
        {picked.kind === "none" && <p className="text-xs text-text-muted">{emptyLabel}</p>}
        {picked.kind === "file" && (
          <>
            <p className="truncate text-sm font-medium text-text">{picked.file.name}</p>
            <p className="text-xs text-text-muted">
              {formatClipLength(picked.file.duration_ms)} · {formatBytes(picked.file.size_bytes)}
            </p>
          </>
        )}
        {picked.kind === "missing" && (
          <p className="text-xs text-warning-11">
            A file that is no longer in the audio store. Choose another.
          </p>
        )}
        {picked.kind === "other" && (
          <p className="truncate text-xs text-text-muted" title={picked.value}>
            On the switch: <span className="font-mono">{picked.value}</span>
          </p>
        )}
      </div>
      {picked.kind === "file" && (
        // `preload="none"`: a form with three pickers does not fetch three files just to show them.
        <audio
          src={picked.file.url}
          controls
          preload="none"
          aria-label={`Listen to ${picked.file.name}`}
          className="h-8 w-48 max-w-full"
        />
      )}
      <Button
        type="button"
        id={id}
        aria-describedby={[summaryId, describedBy].filter(Boolean).join(" ")}
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => setChoosing(true)}
      >
        <AudioLines />
        {picked.kind === "none" ? "Choose" : "Change"}
      </Button>
      {/* "Clear Hold music", after the field's label (`Field` gives it `${id}-label`): a dialog with
          two pickers has two of these. Outside a `Field` it is just "Clear". */}
      {picked.kind !== "none" && (
        <Button
          type="button"
          id={clearId}
          aria-labelledby={id ? `${clearId} ${id}-label` : undefined}
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => onChange("")}
        >
          Clear
        </Button>
      )}
      <AudioChooser
        open={choosing}
        onOpenChange={setChoosing}
        value={value}
        onPick={(url) => {
          onChange(url);
          setChoosing(false);
        }}
      />
    </div>
  );
}

function AudioChooser({
  open, onOpenChange, value, onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onPick: (url: string) => void;
}) {
  const { user } = useAuth();
  const canUpload = atLeast(user?.role, "admin");
  const [search, setSearch] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const query = useDebounced(search, 250);
  const { data: files, isPending, error, refetch } = useAudioLibrary(query, { enabled: open });
  const current = pickedAudio(value, files ?? []);

  React.useEffect(() => {
    if (!open) return;
    setSearch("");
    setUploading(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{uploading ? "Upload a sound" : "Choose a sound"}</DialogTitle>
          <DialogDescription>
            {uploading
              ? "A WAV file. It goes into your audio store and is chosen here."
              : "Files in your audio store. The switch plays the one you choose."}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {uploading ? (
            <UploadAudioForm
              submitLabel="Upload and use"
              onUploaded={(file) => onPick(file.url)}
              onCancel={() => setUploading(false)}
            />
          ) : (
            <>
              <div className="flex gap-2">
                <Input
                  aria-label="Search the audio store"
                  placeholder="Search by name or description"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {canUpload && (
                  <Button type="button" variant="outline" onClick={() => setUploading(true)}>
                    <Upload />
                    Upload
                  </Button>
                )}
              </div>
              {isPending ? (
                <div className="space-y-2">
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
                </div>
              ) : error ? (
                <ErrorState
                  error={error}
                  title="Could not load the audio store"
                  action={
                    <Button type="button" variant="outline" onClick={() => void refetch()}>
                      Try again
                    </Button>
                  }
                />
              ) : files.length === 0 ? (
                <EmptyState
                  icon={AudioLines}
                  title={query ? "No file matches" : "The audio store is empty"}
                  description={
                    query
                      ? "Try another word."
                      : canUpload
                        ? "Upload a WAV file to use it here."
                        : "An admin uploads the files."
                  }
                />
              ) : (
                <ul className="max-h-96 space-y-1.5 overflow-y-auto">
                  {files.map((f) => {
                    const chosen = current.kind === "file" && current.file.id === f.id;
                    return (
                      <li
                        key={f.id}
                        className={cn(
                          "flex flex-wrap items-center gap-2 rounded-md border px-3 py-2",
                          chosen ? "border-accent-8 bg-accent-2" : "border-border",
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-text">{f.name}</p>
                          <p className="truncate text-xs text-text-muted">
                            {formatClipLength(f.duration_ms)}
                            {f.description ? ` · ${f.description}` : ""}
                          </p>
                        </div>
                        <audio
                          src={f.url}
                          controls
                          preload="none"
                          aria-label={`Listen to ${f.name}`}
                          className="h-8 w-44 max-w-full"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant={chosen ? "outline" : "default"}
                          disabled={chosen}
                          aria-label={chosen ? `${f.name} is chosen` : `Use ${f.name}`}
                          onClick={() => onPick(f.url)}
                        >
                          {chosen ? "Chosen" : "Use"}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One WAV into the store: the audio page's upload, and the chooser's.
 *
 * Its submit does not bubble. Opened from a setting's dialog, this form sits inside that dialog's
 * own form in the React tree — a portal moves the DOM, not the tree — and React sends a submit up
 * the tree, so an upload would otherwise also save the line it was opened from.
 */
export function UploadAudioForm({
  onUploaded, onCancel, submitLabel = "Upload",
}: {
  onUploaded: (file: AudioFile) => void;
  onCancel: () => void;
  submitLabel?: string;
}) {
  const upload = useUploadAudio();
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [tags, setTags] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  useDiscardGuard(
    file !== null || name.trim() !== "" || description.trim() !== "" || tags.trim() !== "",
  );

  const choose = (next: File | null) => {
    setFile(next);
    setFileError(next ? audioFileProblem(next) : null);
    if (next && !name.trim()) setName(audioNameFromFile(next.name));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setFormError(null);
    if (!file) {
      setFileError("Choose a WAV file.");
      return;
    }
    const problem = audioFileProblem(file);
    if (problem) {
      setFileError(problem);
      return;
    }
    try {
      const created = await upload.mutateAsync({
        file,
        name: name.trim(),
        description: description.trim(),
        tags: parseTags(tags),
      });
      onUploaded(created);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not upload the file.");
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      {formError && <Callout tone="danger">{formError}</Callout>}
      <Field
        label="File"
        htmlFor="audio-file"
        required
        error={fileError ?? undefined}
        hint="A WAV file, up to 50 MB. MP3 does not play on the phone system: export it as WAV first."
      >
        <input
          type="file"
          accept=".wav,audio/wav,audio/x-wav"
          onChange={(e) => choose(e.target.files?.[0] ?? null)}
          className="block w-full text-xs text-text-muted file:mr-3 file:rounded-md file:border file:border-border file:bg-surface file:px-2.5 file:py-1.5 file:text-xs file:font-medium file:text-text hover:file:bg-surface-hover"
        />
      </Field>
      <Field label="Name" htmlFor="audio-name">
        <Input
          autoComplete="off"
          maxLength={200}
          placeholder="Hold music"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <Field label="Description" htmlFor="audio-description">
        <Textarea
          className="min-h-16 font-sans text-sm"
          maxLength={500}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field label="Tags" htmlFor="audio-tags" hint="Separated by commas: hold music, spanish.">
        <Input autoComplete="off" value={tags} onChange={(e) => setTags(e.target.value)} />
      </Field>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={upload.isPending}>
          Cancel
        </Button>
        <Button type="submit" variant="default" loading={upload.isPending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
